import React, { useState, useEffect, useRef, useMemo } from 'react';
import api from '../utils/apiInterceptor';
import {
    Box,
    Grid,
    Paper,
    TextField,
    Button,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Fade,
    IconButton,
    Slider,
    TableSortLabel,
    TablePagination,
    Tooltip,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { API_URL } from '../constants/data';
import DateRangePicker from '../components/DateRangePicker';
import { Search as SearchIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useSearchHistory } from '../hooks/useSearchHistory';
import 'leaflet/dist/leaflet.css';
import IndiaMap from '../components/IndiaMap';
import { useSearchParams, useLocation, useNavigate, useLoaderData } from 'react-router-dom';
import { id } from 'date-fns/locale';
import GrievanceModal from '../components/Modal';
import InfoButton from '../components/InfoButton';
import InfoModal from '../components/InfoModal';

//todo: here when i navigate to this page from any other page or sidebar then it should fetch automatically if there is any query in filter previously


const INITIAL_FILTERS = {
    query: "",
    // startDate: new Date(new Date().getFullYear(), new Date().getMonth() - 6, new Date().getDate()),
    startDate: new Date("2025-01-01"),
    endDate: new Date(),
    value: 1,
    CityName: "All",
    stateName: "All",
    complaintType: "All",
    complaintMode: "All",
    companyName: "All",
    complaintStatus: "All",
    threshold: 1.3,
    complainNumbers: ["NA"]
};

const CACHE_EXPIRY_HOURS = 24;

const isCacheValid = (cacheTimestamp) => {
    if (!cacheTimestamp) return false;
    const now = Date.now();
    const age = (now - cacheTimestamp) / (1000 * 60 * 60);
    return age < CACHE_EXPIRY_HOURS;
};

const SemanticSearch = () => {
    const [filters, setFilters] = useState(INITIAL_FILTERS);
    const [searchResults, setSearchResults] = useState(null);
    const [totalComplaints, setTotalComplaints] = useState(0);
    const [heatmapData, setHeatmapData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { searchHistory } = useSearchHistory();
    const [open, setOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [modalLoader, setModalLoader] = useState(false);
    const [modalData, setModalData] = useState(null);
    const [openInfo, setOpenInfo] = useState(false);

    // Pagination and Sorting State
    const [orderBy, setOrderBy] = useState('complaintRegDate');
    const [order, setOrder] = useState('desc');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const columns = [
        { id: 'id', label: 'ID' },
        { id: 'fullName', label: 'Name' },
        // { id: 'CityName', label: 'City' },
        { id: 'stateName', label: 'State' },
        // { id: 'complaintType', label: 'Type' },
        { id: 'complaintStatus', label: 'Status' },
        { id: 'complaintRegDate', label: 'Date' },
        { id: 'view', label: 'View Details' }
    ];

    const sortedData = useMemo(() => {
        if (!searchResults || !searchResults.grievanceData) return [];

        const comparator = (a, b) => {
            let aValue = a[orderBy];
            let bValue = b[orderBy];

            // Handle date sorting
            if (orderBy === 'complaintRegDate') {
                aValue = new Date(aValue);
                bValue = new Date(bValue);
            }

            // Handle string sorting (case-insensitive)
            if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (bValue < aValue) {
                return order === 'asc' ? 1 : -1;
            }
            if (bValue > aValue) {
                return order === 'asc' ? -1 : 1;
            }
            return 0;
        };

        return [...searchResults.grievanceData].sort(comparator);
    }, [searchResults, order, orderBy]);

    const paginatedData = useMemo(() => {
        return sortedData;
    }, [sortedData]);

    const getAccessToken = () => {
        return localStorage.getItem("access_token") || "";
    }

    const location = useLocation();

    const handleFilterChange = (name, value) => {
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleRowClick = async (row) => {
        setSelectedRow(row);
        setModalLoader(true);
        setOpen(true);

        try {
            const res = await api.get(
                `${API_URL}/get_complaint_details?complain_number=${row.id}`
            );

            if (res.data) {
                setModalData(res.data);
            } else {
                console.error('No data received from API');
                setModalData(null);
            }
        } catch (error) {
            console.error('Error fetching complaint details:', error);
            setModalData(null);
        } finally {
            setModalLoader(false);
        }
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedRow(null);
        setModalData(null);
    };

    const fetchData = async (pageIndex = page, limit = rowsPerPage, currentFilters = filters, isPagination = false) => {
        setLoading(true);
        setError(null);

        const requestBody = {
            query: currentFilters.query,
            start_date: currentFilters.startDate.toISOString().split('T')[0],
            end_date: currentFilters.endDate.toISOString().split('T')[0],
            value: currentFilters.value,
            purchaseCity: currentFilters.CityName,
            stateName: currentFilters.stateName,
            complaintType: currentFilters.complaintType,
            complaintMode: currentFilters.complaintMode,
            companyName: currentFilters.companyName,
            complaintStatus: currentFilters.complaintStatus,
            threshold: currentFilters.threshold,
            complainNumbers: currentFilters.complainNumbers
        };

        try {
            // If it's just pagination, only fetch search results
            if (isPagination) {
                const searchResponse = await api.post(`${API_URL}/search-company`, {
                    ...requestBody,
                    skip: pageIndex * limit,
                    size: limit
                });
                setSearchResults(searchResponse.data);
                return; // Exit early
            }

            const convertStateDataToObject = (stateDataArray) => {
                if (!Array.isArray(stateDataArray)) return {};

                return stateDataArray.reduce((acc, item) => {
                    // Handle both formats: old API (key/doc_count) and _es API (category/count)
                    const key = item.key || item.category;
                    const count = item.doc_count !== undefined ? item.doc_count : item.count;
                    if (key && key !== 'null' && count !== undefined) {
                        acc[key] = count;
                    }
                    return acc;
                }, {});
            };

            let useCache = false;
            let cachedHeatmap = null;
            let cachedTotalCount = 0;

            // Skip spatial cache when companyName is set — cached data doesn't have company filtering
            const hasCompanyFilter = requestBody.companyName && requestBody.companyName !== "All";

            if (!hasCompanyFilter) {
                if (requestBody.query === "") {
                    const cached = JSON.parse(localStorage.getItem("stateDataCache") || "{}");
                    if (cached?.timestamp && isCacheValid(cached.timestamp)) {
                        cachedHeatmap = convertStateDataToObject(cached.data);
                        cachedTotalCount = Object.values(cachedHeatmap).reduce((a, b) => a + b, 0);
                        useCache = true;
                    }
                } else {
                    const cached = JSON.parse(localStorage.getItem("subStateDataCache") || "{}");
                    if (cached?.timestamp && isCacheValid(cached.timestamp) && cached.prompt === requestBody.query) {
                        cachedHeatmap = convertStateDataToObject(cached.data);
                        useCache = true;
                    }
                }
            }

            if (useCache) {
                const searchResponse = await api.post(`${API_URL}/search-company`, {
                    ...requestBody,
                    skip: pageIndex * limit,
                    size: limit
                });
                setSearchResults(searchResponse.data);
                setHeatmapData(cachedHeatmap);

                if (requestBody.query === "") {
                    setTotalComplaints(cachedTotalCount);
                } else {
                    setTotalComplaints(searchResponse.data.total_count.total_count);
                    console.log("Total Count", searchResponse.data.total_count.total_count);
                }
            } else {
                if (requestBody.query === "") {
                    const [searchResponse, heatmapResponse] = await Promise.all([
                        api.post(`${API_URL}/search-company`, {
                            ...requestBody,
                            skip: pageIndex * limit,
                            size: limit
                        }),
                        api.post(`${API_URL}/complaint_distribution_by_es`,
                            {
                                start_date: requestBody.start_date,
                                end_date: requestBody.end_date,
                                stateName: requestBody.stateName || "All",
                                sectorName: "All",
                                categoryName: "All",
                                purchaseCity: requestBody.purchaseCity || "All",
                                complaintMode: requestBody.complaintMode || "All",
                                companyName: requestBody.companyName || "All",
                                complaintStatus: requestBody.complaintStatus || "All",
                                attribute: "stateName",
                                skip: 0,
                                size: 50
                            },
                        )
                    ]);

                    const heatmapDataObj = convertStateDataToObject(heatmapResponse.data.distribution || []);
                    const total = Object.values(heatmapDataObj).reduce((a, b) => a + b, 0);

                    setSearchResults(searchResponse.data);
                    setHeatmapData(heatmapDataObj);
                    setTotalComplaints(total);
                } else {
                    const [searchResponse, heatmapResponse] = await Promise.all([
                        api.post(`${API_URL}/search-company`, {
                            ...requestBody,
                            skip: pageIndex * limit,
                            size: limit
                        }),
                        api.post(`${API_URL}/get_spatial_analysis_data`,
                            {
                                ...requestBody,
                                companyName: currentFilters.companyName || "All"
                            },
                        )
                    ]);
                    setSearchResults(searchResponse.data);
                    setTotalComplaints(searchResponse.data.total_count.total_count);
                    setHeatmapData(heatmapResponse.data);
                }
            }




            // // Process spatial data to match expected format
            // const processedSpatialData = {};
            // if (Array.isArray(heatmapResponse.data)) {
            //     heatmapResponse.data.forEach(item => {
            //         if (item && item.state && typeof item.count === 'number') {
            //             processedSpatialData[item.state] = item.count;
            //         }
            //     });
            // }
            // console.log("Processed Spatial Data:", processedSpatialData);


        } catch (err) {
            setError('Failed to fetch data: ' + err.message);
        } finally {
            setLoading(false);
        }
    };



    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        fetchData(newPage, rowsPerPage, filters, true);
    };

    const handleChangeRowsPerPage = (event) => {
        const newRows = parseInt(event.target.value, 10);
        setRowsPerPage(newRows);
        setPage(0);
        fetchData(0, newRows, filters, true);
    };


    const hasInitialized = useRef(false);

    // Unified initialization: handles both search history and company navigation
    useEffect(() => {
        if (hasInitialized.current) return;
        if (!searchHistory) return; // Wait for searchHistory to load

        hasInitialized.current = true;

        // Start with current filters
        let newFilters = { ...filters };
        let shouldFetch = false;

        // 1. Restore from search history if available
        if (searchHistory?.length > 0) {
            const lastQuery = searchHistory[searchHistory.length - 1];
            const queryParams = lastQuery?.params;

            if (queryParams) {
                newFilters = {
                    ...newFilters,
                    query: queryParams?.query || '',
                    startDate: new Date(queryParams?.start_date),
                    endDate: new Date(queryParams?.end_date),
                    threshold: queryParams?.threshold,
                    value: queryParams?.value,
                    complainNumbers: queryParams?.complaint_numbers || ['NA']
                };

                if (newFilters.query) {
                    shouldFetch = true;
                }
            }
        }

        // 2. Apply company name from navigation (overrides/adds to history filters)
        if (location.state?.fromCompanies && location.state?.companyName) {
            newFilters.companyName = location.state.companyName;
            shouldFetch = true; // Always fetch when navigated from Companies
        }

        // 3. Set filters and fetch once with all combined filters
        setFilters(newFilters);

        if (shouldFetch) {
            fetchData(0, rowsPerPage, newFilters);
        }
    }, [searchHistory, location.state]);




    return (
        <Fade in timeout={500}>
            <Box sx={{ pt: 1, px: 3 }}>
                {/* <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
                    Semantic Search
                </Typography> */}

                {/* Filters */}
                <Box
                    sx={{
                        py: 1,
                        mb: 1,
                    }}
                >
                    <Grid container spacing={4} alignItems="center">
                        {/* Row 1: Search Query + Date Range */}
                        <Grid item xs={12} md={5}>
                            <TextField
                                fullWidth
                                label="Search Query"
                                value={filters.query}
                                onChange={(e) => handleFilterChange('query', e.target.value)}
                                variant="outlined"
                                placeholder="Enter keywords to search..."
                                InputProps={{
                                    endAdornment: (
                                        <IconButton
                                            onClick={() => {
                                                setPage(0);
                                                fetchData(0, rowsPerPage);
                                            }}
                                            disabled={loading}
                                            size="small"
                                        >
                                            <SearchIcon />
                                        </IconButton>
                                    )
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <DateRangePicker
                                value={{ startDate: filters.startDate, endDate: filters.endDate }}
                                onChange={(val) => {
                                    if (val) {
                                        handleFilterChange('startDate', val.startDate ? new Date(val.startDate) : filters.startDate);
                                        handleFilterChange('endDate', val.endDate ? new Date(val.endDate) : filters.endDate);
                                    }
                                }}
                            />
                        </Grid>

                        {/* Row 2: Company Name + Search Type + Relevance Slider */}
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Company Name"
                                value={filters.companyName || ''}
                                onChange={(e) => handleFilterChange('companyName', e.target.value)}
                                variant="outlined"
                                placeholder="Enter company name..."
                            />
                        </Grid>
                        <Grid item xs={6} md={2}>
                            <FormControl fullWidth>
                                <InputLabel>Search Type</InputLabel>
                                <Select
                                    value={filters.value}
                                    label="Search Type"
                                    onChange={(e) => handleFilterChange('value', e.target.value)}
                                >
                                    <MenuItem value={1}>Semantic</MenuItem>
                                    <MenuItem value={2}>Keyword</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <Typography variant="caption" sx={{ mb: -0.5, display: 'block' }}>Relevance</Typography>
                            <Slider
                                value={filters.threshold}
                                onChange={(_, value) => handleFilterChange('threshold', value)}
                                valueLabelDisplay="auto"
                                sx={{
                                    color: 'rgba(94, 79, 170, 1)',
                                    '& .MuiSlider-thumb': {
                                        backgroundColor: 'rgba(94, 79, 170, 1)',
                                        width: 16,
                                        height: 16,
                                    },
                                    '& .MuiSlider-track': {
                                        backgroundColor: 'rgba(94, 79, 170, 1)',
                                    },
                                    '& .MuiSlider-rail': {
                                        backgroundColor: '#d8d8d8',
                                    }
                                }}
                                step={0.1}
                                marks={[
                                    { value: 1, label: 'Low' },
                                    { value: 2, label: 'High' },
                                ]}
                                min={1}
                                max={2}
                            />
                        </Grid>

                        {/* Row 3: Search Button + Results Count */}
                        <Grid item xs={12} md={3} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    setPage(0);
                                    fetchData(0, rowsPerPage);
                                }}
                                disabled={loading}
                                startIcon={<SearchIcon />}
                                sx={{
                                    width: { xs: '100%', md: 'auto' },
                                    height: { xs: 32, md: 34, lg: 40, xl: 46 },
                                    backgroundColor: 'rgba(94, 79, 170, 1)',
                                    color: '#fff',
                                    textTransform: 'none',
                                    '&:hover': {
                                        backgroundColor: '#9B86FF',
                                    }
                                }}
                            >
                                {loading ? 'Searching...' : 'Search'}
                            </Button>
                            {searchResults && (
                                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                    {totalComplaints.toLocaleString()} complaints
                                </Typography>
                            )}
                        </Grid>
                    </Grid>
                </Box>

                {error && (
                    <Alert
                        severity="error"
                        sx={{
                            mb: 3,
                            borderRadius: 2
                        }}
                    >
                        {error}
                    </Alert>
                )}

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        // gap: 2,
                        width: '100%'
                    }}
                >
                    {/* Map Section */}
                    {heatmapData && (
                        <Paper
                            elevation={3}
                            sx={{
                                width: '45%',
                                // p: 2,
                                mb: 3,
                                height: '820px',
                                borderRadius: 2,
                                overflow: 'scroll',
                                position: 'relative'
                            }}
                        >
                            {/* <Typography variant="h6" gutterBottom>
                                Spatial Distribution of Complaints
                            </Typography> */}
                            <IndiaMap data={heatmapData} />
                            {/* <MapLegend /> */}
                        </Paper>
                    )}

                    {/* Results Table with improved styling */}
                    {searchResults && (
                        <Paper
                            elevation={3}
                            sx={{
                                borderRadius: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                maxHeight: '820px',
                                maxWidth: '100%',
                                minHeight: '790px',
                                width: '54%',
                                overflow: 'hidden'
                            }}
                        >
                            <TableContainer
                                sx={{
                                    flex: 1,
                                    overflow: 'auto',
                                    '& .MuiTableHead-root': {
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 1,
                                        backgroundColor: '#f5f5f5'
                                    }
                                }}
                            >
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                            {columns.map((column) => (
                                                <TableCell key={column.id}>
                                                    <TableSortLabel
                                                        active={true}
                                                        direction={orderBy === column.id ? order : 'asc'}
                                                        onClick={() => handleRequestSort(column.id)}
                                                    >
                                                        {column.label}
                                                    </TableSortLabel>
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {paginatedData.map((row) => (
                                            <Tooltip
                                                key={row.complaintNumber}
                                                title={
                                                    <Typography sx={{
                                                        p: 3,
                                                        fontSize: '1.1rem',
                                                        maxWidth: 800,
                                                        minWidth: 600,
                                                        whiteSpace: 'pre-wrap',
                                                        lineHeight: 1.5,
                                                        color: '#333',
                                                    }}>
                                                        {/* {row.fullName} - {row.complaintDetails || 'No details available'} */}
                                                        {row.consumerName} - {row.complaintDetails || 'No details available'}

                                                    </Typography>
                                                }
                                                placement="top"
                                                arrow
                                                componentsProps={{
                                                    tooltip: {
                                                        sx: {
                                                            bgcolor: '#E1DAEE',
                                                            '& .MuiTooltip-arrow': {
                                                                color: '#E1DAEE'
                                                            },
                                                            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
                                                            maxWidth: 'none'
                                                        }
                                                    }
                                                }}
                                            >
                                                <TableRow
                                                    key={row.complaintNumber}
                                                    hover
                                                    sx={{
                                                        backgroundColor: '#fafafa',
                                                        cursor: 'pointer',
                                                        transition: 'background-color 0.2s',
                                                        '&:hover': {
                                                            backgroundColor: '#e0cbf5ff'
                                                        }
                                                    }}
                                                >
                                                    <TableCell>{row.id}</TableCell>
                                                    {/* <TableCell>{row.fullName}</TableCell> */}
                                                    <TableCell>{row.consumerName}</TableCell>
                                                    {/* <TableCell>{row.CityName}</TableCell> */}
                                                    <TableCell>{row.stateName}</TableCell>
                                                    {/* <TableCell>{row.complaintType}</TableCell> */}
                                                    <TableCell>{row.complaintStatus}</TableCell>
                                                    <TableCell>
                                                        {new Date(row.complaintRegDate).toLocaleDateString()}
                                                    </TableCell>
                                                    <TableCell sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <IconButton
                                                            color="primary"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleRowClick(row);
                                                            }}
                                                            size="small"
                                                        >
                                                            <VisibilityIcon />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            </Tooltip>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                rowsPerPageOptions={[10, 20, 50, 100]}
                                component="div"
                                count={totalComplaints || 0}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                sx={{
                                    borderTop: '1px solid rgba(224, 224, 224, 1)',
                                    flexShrink: 0
                                }}
                            />
                        </Paper>
                    )}
                </Box>
                {loading && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            mt: 3,
                            p: 3
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}
                {open && (
                    <>
                        {modalLoader ? (
                            <Box
                                sx={{
                                    position: 'fixed',
                                    top: 0,
                                    left: 0,
                                    width: '100vw',
                                    height: '100vh',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    backgroundColor: 'rgba(0,0,0,0.2)',
                                    zIndex: 9999,
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        ) : (
                            modalData && (
                                <GrievanceModal
                                    open={open}
                                    onClose={handleClose}
                                    modalData={modalData}
                                    modalLoader={modalLoader}
                                />
                            )
                        )}
                    </>
                )}
            </Box>
        </Fade>
    );
};

export default SemanticSearch;